<template>
    <div class="qualification">
        <section class="qualification-header">
            <VJoyWrapper :no-margin="true" justify="space-between">
                <VJoyWrapper justify="space-between" :no-margin="true" column-gap="4">
                    <template v-if="isAllowedToEdit">
                        <VJoyWrapper v-if="profileQualificationStore.inEditMode" :no-margin="true" column-gap="2">
                            <EditionCancelButton />
                            <EditionSaveButton v-if="profileQualificationStore.formHasChanged" />
                        </VJoyWrapper>
                        <EditionStartButton v-else />
                    </template>
                    <template v-else>
                        <VJoyTooltip>
                            <VJoyButton disabled icon="pen" size="xsmall" :label="$t('profile.vetting.drawer.edit')" />
                            <template #tooltip-content>
                                {{ $t('profile.vetting.drawer.edit.disabled') }}
                            </template>
                        </VJoyTooltip>
                    </template>
                </VJoyWrapper>
                <VJoyWrapper justify="space-between" :no-margin="true" column-gap="2">
                    <VJoyWrapper v-if="isStrategyPending" :no-margin="true" column-gap="2" data-testid="vetting-is-strategy-pending">
                        <VJoyButton
                            v-if="showVerticalButtons"
                            :loading="!!loading"
                            variant="secondary"
                            size="xsmall"
                            :label="$t('profile.vetting.community.vetting.decision.redirect')"
                            :disabled="!enableVerticalStrategySwitch"
                            data-testid="vetting-vertical-refuse-strategy"
                            @click="handleRefuseToStrategy"
                        />
                        <VJoyButton
                            v-if="showVerticalButtons"
                            :loading="!!loading"
                            variant="primary"
                            size="xsmall"
                            :label="$t('profile.vetting.community.vetting.decision.accept')"
                            :disabled="!enableVerticalStrategySwitch"
                            data-testid="vetting-vertical-accept-strategy"
                            @click="handleAcceptToStrategy"
                        />
                    </VJoyWrapper>
                    <Transition>
                        <AdminLinks v-if="!profileQualificationStore.inEditMode && qualification.links" :links="qualification.links" />
                    </Transition>
                </VJoyWrapper>
            </VJoyWrapper>
        </section>

        <section v-if="availabilityMessage || moderationMessage" class="sourcing-banner">
            <VJoyBadge
                v-if="availabilityMessage"
                bulletpoint
                :label="availabilityMessage"
                variant="orange"
                class="sourcing-banner__availability-badge"
                @click="goToMissionPageIfAny"
            />
            <VJoyHighlight v-else-if="moderationMessage" accent display-icon icon="warning-triangle" level="warning">
                {{ moderationMessage }}
            </VJoyHighlight>
        </section>

        <VJoyCollapse class="collapse">
            <VJoyWrapper justify="space-between" no-margin>
                <h3 class="activity" data-testid="qualification-tab-activity-section">
                    {{ $t('profile.vetting.qualification.activity.title') }}
                </h3>
                <VJoyButton
                    v-if="qualification.links"
                    size="xsmall"
                    variant="ghost"
                    icon="pen"
                    :href="`${qualification.links.shakePage}/timeline`"
                    target="_blank"
                >
                    {{ $t('profile.vetting.qualification.personal.information.timeline.cta') }}
                </VJoyButton>
            </VJoyWrapper>

            <VJoyCollapseItem :header="$t('profile.admin.compliance.title')" open>
                <VJoyWrapper direction="row" wrap="nowrap" row-gap="2" no-margin>
                    <VJoyHighlight display-icon :level="isCompanyComplianceValid ? 'success' : 'error'">
                        <VJoyWrapper direction="row" wrap="nowrap" justify="space-between" column-gap="2" no-margin>
                            {{ t('profile.admin.compliance.company.subtitle') }}
                            <VJoyTooltip position="bottom-end" event="mouseenter">
                                <VJoyIcon :aria-label="$t('general.info')" role="button" size="xsmall" name="info-circle" color="neutral" />
                                <template #tooltip-content>{{ t('profile.admin.compliance.company.tooltip') }}</template>
                            </VJoyTooltip>
                        </VJoyWrapper>
                        <VJoyLink :href="shakeComplianceUrl" target="_blank">{{ t('profile.admin.compliance.link.message') }}</VJoyLink>
                    </VJoyHighlight>
                    <VJoyHighlight display-icon :level="isCompanyComplianceUpToDate ? 'success' : 'error'">
                        <VJoyWrapper direction="row" wrap="nowrap" justify="space-between" column-gap="2" no-margin>
                            {{ t('profile.admin.compliance.documents.subtitle') }}
                            <VJoyTooltip position="bottom-end" event="mouseenter">
                                <VJoyIcon :aria-label="$t('general.info')" role="button" size="xsmall" name="info-circle" color="neutral" />
                                <template #tooltip-content>{{ t('profile.admin.compliance.documents.tooltip') }}</template>
                            </VJoyTooltip>
                        </VJoyWrapper>
                        <VJoyLink :href="shakeComplianceUrl" target="_blank">{{ t('profile.admin.compliance.link.message') }}</VJoyLink>
                    </VJoyHighlight>
                </VJoyWrapper>
            </VJoyCollapseItem>

            <VJoyCollapseItem :header="$t('profile.vetting.qualification.personal.information.title')" open>
                <VJoyWrapper no-margin>
                    <EmailAddressField />
                    <PhoneNumberField />
                </VJoyWrapper>
            </VJoyCollapseItem>

            <VJoyCollapseItem open :header="$t('profile.vetting.qualification.preferences.title')">
                <VJoyWrapper no-margin v-bind="{align: 'stretch', direction: 'column', rowGap: '4'}">
                    <AvailabilityField />
                    <MobilityField />
                    <DailyRateField />
                    <LimitationsField />
                </VJoyWrapper>
            </VJoyCollapseItem>

            <VJoyCollapseItem :header="$t('profile.vetting.qualification.skills.assessment.title')" open>
                <VJoyWrapper no-margin v-bind="{align: 'stretch', direction: 'column', rowGap: '4'}">
                    <template v-if="isStrategyPendingOrAccepted">
                        <InternalSeniorityLevelField />
                        <IndustryExpertiseField />
                        <FunctionalExpertiseField />
                        <OtherExpertiseField />
                        <SoftSkillsField />
                    </template>
                    <template v-else>
                        <OtherExpertiseField />
                        <SoftSkillsField />
                        <IndustryExpertiseField />
                    </template>
                </VJoyWrapper>
            </VJoyCollapseItem>

            <VJoyCollapseItem open :header="$t('profile.vetting.qualification.career.path.title')">
                <VJoyWrapper no-margin v-bind="{align: 'stretch', direction: 'column', rowGap: '4'}">
                    <ProfessionalInterestsField />
                    <InterestInInterimField v-if="isStrategyPendingOrAccepted" />
                </VJoyWrapper>
            </VJoyCollapseItem>

            <VJoyCollapseItem v-if="isStrategyPendingOrAccepted" :header="$t('profile.vetting.qualification.professional.background.title')" open>
                <VJoyWrapper no-margin v-bind="{align: 'stretch', direction: 'column', rowGap: '4'}">
                    <ProfessionalBackgroundField />
                    <OtherProfessionalBackgroundField />
                </VJoyWrapper>
            </VJoyCollapseItem>

            <VJoyCollapseItem :header="$t('profile.vetting.qualification.community.title')" open>
                <VJoyWrapper no-margin v-bind="{align: 'stretch', direction: 'column', rowGap: '4'}">
                    <CommunityMetadataField />
                    <FreelancerVerticalStateField />
                    <EngagementCommentField />
                </VJoyWrapper>
            </VJoyCollapseItem>

            <VJoyCollapseItem
                v-if="isStrategyPendingOrAccepted && qualification.comatchLegacyNotes"
                :header="$t('profile.vetting.qualification.comatch_legacy_notes.title')"
                open
            >
                <div class="collapse-detail">
                    <ComatchLegacyNotesField />
                </div>
            </VJoyCollapseItem>
        </VJoyCollapse>
    </div>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {navigateTo} from '@typed-router';
    import {storeToRefs} from 'pinia';
    import {useHasEnoughPriviledge, useTranslation} from '#imports';
    import {
        VJoyBadge,
        VJoyButton,
        VJoyCollapse,
        VJoyCollapseItem,
        VJoyHighlight,
        VJoyIcon,
        VJoyLink,
        VJoyTooltip,
        VJoyWrapper,
    } from '@maltjoy/core-vue';
    import AdminLinks from '~/components/admin-section-tabs/qualification/AdminLinks.vue';
    import AvailabilityField from '~/components/admin-section-tabs/qualification/AvailabilityField.vue';
    import EditionCancelButton from '~/components/admin-section-tabs/qualification/buttons/EditionCancelButton.vue';
    import EditionSaveButton from '~/components/admin-section-tabs/qualification/buttons/EditionSaveButton.vue';
    import EditionStartButton from '~/components/admin-section-tabs/qualification/buttons/EditionStartButton.vue';
    import ComatchLegacyNotesField from '~/components/admin-section-tabs/qualification/ComatchLegacyNotesField.vue';
    import CommunityMetadataField from '~/components/admin-section-tabs/qualification/CommunityMetadataField.vue';
    import DailyRateField from '~/components/admin-section-tabs/qualification/DailyRateField.vue';
    import EmailAddressField from '~/components/admin-section-tabs/qualification/EmailAddressField.vue';
    import EngagementCommentField from '~/components/admin-section-tabs/qualification/EngagementCommentField.vue';
    import FreelancerVerticalStateField from '~/components/admin-section-tabs/qualification/FreelancerVerticalStateField.vue';
    import FunctionalExpertiseField from '~/components/admin-section-tabs/qualification/FunctionalExpertiseField.vue';
    import IndustryExpertiseField from '~/components/admin-section-tabs/qualification/IndustryExpertiseField.vue';
    import InterestInInterimField from '~/components/admin-section-tabs/qualification/InterestInInterimField.vue';
    import InternalSeniorityLevelField from '~/components/admin-section-tabs/qualification/InternalSeniorityLevelField.vue';
    import LimitationsField from '~/components/admin-section-tabs/qualification/LimitationsField.vue';
    import MobilityField from '~/components/admin-section-tabs/qualification/MobilityField.vue';
    import OtherExpertiseField from '~/components/admin-section-tabs/qualification/OtherExpertiseField.vue';
    import OtherProfessionalBackgroundField from '~/components/admin-section-tabs/qualification/OtherProfessionalBackgroundField.vue';
    import PhoneNumberField from '~/components/admin-section-tabs/qualification/PhoneNumberField.vue';
    import ProfessionalBackgroundField from '~/components/admin-section-tabs/qualification/ProfessionalBackgroundField.vue';
    import ProfessionalInterestsField from '~/components/admin-section-tabs/qualification/ProfessionalInterestsField.vue';
    import SoftSkillsField from '~/components/admin-section-tabs/qualification/SoftSkillsField.vue';
    import {eventBus} from '~/config/events';
    import {useFreelancerVerticalStore} from '~/store/freelancerVerticalStore';
    import {useProfileQualificationStore} from '~/store/profileQualificationStore';
    import {useProfileStore} from '~/store/profileStore';

    const {t} = useTranslation();

    const enableVerticalStrategySwitch = ref(false);
    useHasEnoughPriviledge(['ROLE_VETTING']).then((enabled) => (enableVerticalStrategySwitch.value = enabled));

    const profileStore = useProfileStore();
    const profileQualificationStore = useProfileQualificationStore();
    const {profile, verificationChecklist} = storeToRefs(profileStore);
    const {qualification, isAllowedToEdit} = storeToRefs(profileQualificationStore);

    const freelancerVerticalStore = useFreelancerVerticalStore();
    const {isStrategyPendingOrAccepted, isStrategyPending, loading} = storeToRefs(freelancerVerticalStore);

    const showVerticalButtons = computed(() => freelancerVerticalStore.verticalChangesEnabled && !profileQualificationStore.inEditMode);
    const availabilityMessage = computed(() => {
        switch (qualification.value.availabilityStatus) {
            case 'ON_PROJECT':
                return t('profile.vetting.qualification.availability.on.project');
            case 'ON_SELECTION_LIST':
                return t('profile.vetting.qualification.availability.on.selection.list');
            default:
                return undefined;
        }
    });

    const availabilityCursor = computed(() => (qualification.value.currentProjectId ? 'pointer' : undefined));
    const moderationMessage = computed(() => {
        if (qualification.value.sourcingStatus.eligible) {
            return undefined;
        }
        const reasons = (qualification.value.sourcingStatus?.reasons || []).map((r) => t(`profile.vetting.qualification.sourcing.${r}`)).join(', ');
        return `${t('profile.vetting.qualification.sourcing.not.eligible')} ${reasons}`;
    });
    const isCompanyComplianceValid = computed(() => verificationChecklist.value?.complianceCheck.companyVerified || false);
    const isCompanyComplianceUpToDate = computed(() => verificationChecklist.value?.complianceCheck.companyComplianceUpToDate || false);
    const shakeComplianceUrl = computed(() => {
        if (profile.value?.accountId) {
            return `/crm/app/freelancers/${profile.value.accountId}/finance`;
        }
        return undefined;
    });

    function handleAcceptToStrategy() {
        freelancerVerticalStore.acceptToStrategy();
    }

    function handleRefuseToStrategy() {
        eventBus.emit('verticalRefuseToStrategy:open');
    }

    async function goToMissionPageIfAny() {
        if (qualification.value.currentProjectId) {
            await navigateTo(`/project-freelancer/project/${qualification.value.currentProjectId}`, {external: true, open: {target: '_blank'}});
        }
    }
</script>

<style lang="scss" scoped>
    // TODO check DS tokens
    .v-enter-active,
    .v-leave-active {
        transition: opacity 0.5s ease;
    }

    .v-enter-from,
    .v-leave-to {
        opacity: 0;
    }

    .qualification {
        padding-right: 10px;
        margin-bottom: 20px;

        &-highlight-warning {
            margin: 0 0 var(--joy-core-spacing-4) 0;
        }
    }

    .collapse {
        margin: 5px;
    }

    .activity {
        font-size: var(--collapse-item-summary-font-size, var(--collapse-item-font-size));
        font-weight: var(--joy-font-weight-bold);
    }

    .community-content {
        padding-top: 12px;
    }

    .qualification-header {
        background-color: var(--joy-color-neutral-0);
        position: sticky;
        top: 0;
        z-index: 2;
        text-align: right;
        padding: var(--joy-core-spacing-4) 0;
        border-bottom: 1px solid var(--joy-color-primary-30);
    }

    .sourcing-banner {
        padding-top: var(--joy-core-spacing-4);

        &__availability-badge {
            cursor: v-bind(availabilityCursor);
        }
    }
</style>
